import { Autocomplete, TextField } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { iclApi, taskApi } from 'api';
import { subjectsApi } from 'api/subjects-api';
import { useModal } from 'shared/hooks';
import { Exam, Option, WorkType } from 'shared/types';
import { checkListCreateSchema, SummarizeTask, Task } from 'shared/types/task';
import { SendToStudentsModal } from 'shared/ui';

import { classArray } from '../../constants/classArray';
import { getUser, getUserType } from '../../redux/slices/userSlice';
import { getTeachersSubjects } from '../../utils/getTeachersSubjects';
import ExamTasksForm from '../ExamTasksForm';
import { LoadingError } from '../LoadingError';

import './style.scss';

interface Props {
  updateId?: string;
}

export default function ExamForm({ updateId }: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();
  const userTypeSelector = useSelector(getUserType);
  const user = useSelector(getUser);
  const { isOpen, close, open } = useModal();

  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [teachersSubjects, setTeachersSubjects] = useState<string[] | null>(null);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);
  const [currentICL, setCurrentICL] = useState<Exam | null>(null);
  const [updateIclInitValue, setUpdateIclInitValue] = useState<Exam | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);

  const formik = useFormik({
    initialValues: {
      theme: updateIclInitValue?.theme ?? '',
      checkListNumber: updateIclInitValue?.number ?? '',
      courseGoal: updateIclInitValue?.courseGoal ?? '',
      courseName: updateIclInitValue?.courseName ?? '',
      subject: updateIclInitValue?.subject ?? null,
      language: updateIclInitValue?.language ?? '',
      studentClass: updateIclInitValue?.class ?? '',
    },
    enableReinitialize: !!updateId,
    validationSchema: checkListCreateSchema,
    onSubmit: async values => {
      if (!values.subject) {
        return;
      }

      const newICL: Omit<Exam, 'id'> = {
        class: values.studentClass,
        courseGoal: values.courseGoal,
        courseName: values.courseName,
        createdAt: updateIclInitValue?.createdAt ?? new Date().getTime(),
        isActive: true,
        isTemplate: setIsTemplate(),
        teacherEmail: setTeachersEmailToIcl(),
        language: values.language,
        number: +values.checkListNumber,
        subject: values.subject,
        theme: values.theme,
        type: WorkType.exam,
        isConsideration: updateIclInitValue?.isConsideration ?? false,
      };

      try {
        if (updateId || currentICL) {
          await iclApi.updateICL({ ...newICL, id: updateId ?? currentICL!.id });
        } else {
          const ICL = await iclApi.addICL(newICL);
          setCurrentICL(ICL);

          toast.success(t('Successfully added!'));
        }
      } catch (e) {
        console.log(e);
        toast.error(t('Something went wrong. Try again!'));
      }
    },
  });

  const setIsTemplate = () => {
    if (userTypeSelector === 'Teacher') {
      return false;
    } else if (userTypeSelector === 'NewAdmin' || userTypeSelector === 'Superadmin') {
      return updateIclInitValue?.isTemplate ?? false;
    } else {
      return true;
    }
  };

  const setTeachersEmailToIcl = () => {
    if (userTypeSelector === 'Teacher') {
      return user.mail;
    }
    if (userTypeSelector === 'NewAdmin' || userTypeSelector === 'Superadmin') {
      return updateIclInitValue ? updateIclInitValue?.teacherEmail : '';
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (user) {
      getTeachersSubjects(user.mail).then(subjects => {
        setTeachersSubjects(subjects);
      });
    }
  }, [user]);

  useEffect(() => {
    if (teachersSubjects) {
      const updateSubjects = (subjects: Option[]) => {
        const filteredSubjects = subjects.filter(subject => teachersSubjects.includes(subject.name || subject.ruName));

        if (userTypeSelector === 'Teacher') {
          setSubjects(filteredSubjects);
        } else {
          setSubjects(subjects);
        }
      };
      const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

      if (updateId) {
        iclApi.getICL(updateId).then(data => setUpdateIclInitValue(data));
      }

      setLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [teachersSubjects]);

  useEffect(() => {
    if (updateIclInitValue && updateId) {
      setCurrentICL(updateIclInitValue);

      const setData = (data: Task[]) => setTasks(data);
      const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: updateId, setNewTasks: setData });

      return () => {
        unsubscribe();
      };
    }
  }, [updateIclInitValue]);

  const openSendToStudentsModal = () => open();
  const isSendToStudentsButtonDisabled = !subjects || !tasks.length;

  return (
    <LoadingError isLoading={isLoading}>
      <FormikProvider value={formik}>
        <Form>
          <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-2 py-8 shadow-none lg:w-11/12 lg:!px-[111px] lg:!py-10">
            <div className="flex flex-col gap-6 lg:gap-8">
              <div className="lg:w-[674px]">
                <div className="relative !mb-3 lg:flex lg:items-center lg:gap-3">
                  <p className="!mb-[6px] inline-block min-w-fit text-2xl font-bold lg:!mb-2 lg:text-5xl">
                    {t('exam form number')} №
                  </p>

                  <input
                    id="checkListNumber"
                    className={`w-full grow border-b-2 text-2xl font-bold placeholder:font-normal lg:grow-0 lg:text-5xl ${formik.errors.checkListNumber ? 'border-red-600' : 'border-black'} bg-transparent focus:outline-none`}
                    value={formik.values.checkListNumber}
                    onChange={event => formik.setFieldValue('checkListNumber', event.target.value)}
                    maxLength={5}
                  />

                  {!!formik.errors.checkListNumber && (
                    <p className="absolute -bottom-[18px] left-0 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:left-[517px]">
                      {t(formik.errors.checkListNumber)}
                    </p>
                  )}
                </div>

                <div className="relative !mb-3 lg:flex lg:items-center lg:gap-3">
                  <p className="!mb-[6px] inline-block min-w-fit text-xl font-bold lg:!mb-2 lg:text-[32px] lg:leading-[44px]">
                    {t('exam form name')}:{' '}
                  </p>

                  <input
                    id="courseName"
                    className={`w-full border-b-2 text-xl focus:outline-none lg:text-[32px] lg:leading-[44px] ${formik.errors.courseName ? 'border-red-600' : 'border-black'} bg-transparent`}
                    value={formik.values.courseName}
                    onChange={event => formik.setFieldValue('courseName', event.target.value)}
                    placeholder={t('Enter the name')}
                  />

                  {!!formik.errors.courseName && (
                    <p className="absolute -bottom-[18px] left-0 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:left-[220px]">
                      {t(formik.errors.courseName)}
                    </p>
                  )}
                </div>

                <div className="relative !mb-0 lg:flex lg:items-center lg:gap-3">
                  <p className="!mb-[6px] inline-block min-w-fit text-lg font-bold focus:outline-none lg:!mb-2 lg:text-2xl">
                    {t('exam form theme')}:
                  </p>

                  <input
                    id="theme"
                    className={`w-full border-b-2 text-lg focus:outline-none lg:text-2xl ${formik.errors.theme ? 'border-red-600' : 'border-black'} bg-transparent`}
                    value={formik.values.theme}
                    onChange={event => formik.setFieldValue('theme', event.target.value)}
                    placeholder={t('Enter the name')}
                  />

                  {!!formik.errors.theme && (
                    <p className="absolute -bottom-[18px] left-0 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:left-[152px]">
                      {t(formik.errors.theme)}
                    </p>
                  )}
                </div>
              </div>

              <div className="purpose-wrap">
                <div className="relative !mb-4 lg:!mb-6">
                  <p className="mb-[6px] text-sm font-bold uppercase lg:mb-2 lg:text-lg">
                    {t('exam form purpose of the course')}:
                  </p>

                  <textarea
                    value={formik.values.courseGoal}
                    className={`h-[30px] w-full pt-0 text-lg lg:text-[22px] ${formik.errors.courseGoal ? 'border-red-600' : 'border-black'} border-x-0 border-b-2 border-t-0`}
                    onChange={event => formik.setFieldValue('courseGoal', event.target.value)}
                    placeholder={t('Enter the text')}
                  />

                  {!!formik.errors.courseGoal && (
                    <p className="absolute -bottom-[18px] left-0 !mb-0 inline-block h-fit text-sm font-medium text-red-600">
                      {t(formik.errors.courseGoal)}
                    </p>
                  )}
                </div>

                <div className="flex flex-col !gap-4 lg:flex-row lg:gap-6">
                  <div
                    className={`relative w-full border-b ${formik.errors.studentClass ? 'border-red-600' : 'border-black'} lg:w-2/4`}
                  >
                    <p className="mb-[6px] text-sm font-bold uppercase lg:mb-2 lg:text-lg">{t('class')}:</p>

                    <Autocomplete
                      id="studentClass"
                      autoComplete={false}
                      options={classArray}
                      getOptionLabel={option => t(option)}
                      value={formik.values.studentClass}
                      onChange={(e, v) => formik.setFieldValue('studentClass', v)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: 0,
                        },
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          className="border-none focus:outline-none"
                          value={t(formik.values.studentClass)}
                          error={!!formik.errors.studentClass}
                          onChange={e => formik.setFieldValue('studentClass', e.target.value)}
                          placeholder={t('Choose a class')}
                          variant="standard"
                          sx={{
                            '& .MuiInputBase-input': {
                              marginLeft: 0,
                            },
                          }}
                        />
                      )}
                    />

                    {!!formik.errors.studentClass && (
                      <p className="absolute -bottom-5 !mb-0 inline-block h-fit text-sm font-medium text-red-600">
                        {t(formik.errors.studentClass)}
                      </p>
                    )}
                  </div>

                  <div
                    className={`relative w-full border-b ${formik.errors.language ? 'border-red-600' : 'border-black'} lg:w-2/4`}
                  >
                    <p className="mb-[6px] text-sm font-bold uppercase lg:mb-2 lg:text-lg">{t('language')}:</p>

                    <Autocomplete
                      id="language"
                      options={['UA', 'EN', 'DE', 'RU']}
                      value={formik.values.language}
                      onChange={(e, v) => formik.setFieldValue('language', v)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: 0,
                        },
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={!!formik.errors.language}
                          className="focus:outline-none"
                          value={formik.values.language}
                          onChange={e => formik.setFieldValue('language', e.target.value)}
                          placeholder={t('Choose a language')}
                          variant="standard"
                          sx={{
                            '& .MuiInputBase-input': {
                              marginLeft: 0,
                            },
                          }}
                        />
                      )}
                    />

                    {!!formik.errors.language && (
                      <p className="absolute -bottom-5 !mb-0 inline-block h-fit text-sm font-medium text-red-600">
                        {t(formik.errors.language)}
                      </p>
                    )}
                  </div>

                  {subjects && (
                    <div
                      className={`relative w-full border-b ${formik.errors.subject ? 'border-red-600' : 'border-black'} lg:w-2/4`}
                    >
                      <p className="mb-[6px] text-sm font-bold uppercase lg:mb-2 lg:text-lg">{t('subject')}:</p>

                      <Autocomplete
                        id="subject"
                        options={subjects.sort((a, b) => a.name.localeCompare(b.name)) as Option[]}
                        value={formik.values.subject}
                        onChange={(event, value) => formik.setFieldValue('subject', value)}
                        getOptionLabel={option => (platformLang === 'ua' ? option.name : option.ruName)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            padding: 0,
                          },
                        }}
                        renderInput={params => (
                          <TextField
                            className="border-0 focus:outline-none"
                            {...params}
                            placeholder={t('Choose a subject')}
                            error={!!formik.errors.subject}
                            value={formik.values.subject?.[platformLang === 'ua' ? 'name' : 'ruName']}
                            variant="standard"
                            sx={{
                              '& .MuiInputBase-input': {
                                marginLeft: 0,
                              },
                            }}
                          />
                        )}
                      />

                      {!!formik.errors.subject && (
                        <p className="absolute -bottom-5 !mb-0 inline-block h-fit text-sm font-medium text-red-600">
                          {t(formik.errors.subject)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:gap-6">
                <button
                  className="btn btn-primary more lg:w-2/6"
                  type="submit"
                  onClick={() => {
                    if (currentICL || updateId) {
                      toast.success(t('Successfully edited!'));
                    }
                  }}
                  disabled={!subjects}
                >
                  <p className="!mb-0 text-lg font-bold">{t('save')}</p>
                </button>

                <button
                  className="btn btn-primary more lg:w-2/6"
                  type="button"
                  onClick={openSendToStudentsModal}
                  disabled={isSendToStudentsButtonDisabled}
                >
                  <p className="!mb-0 text-lg font-bold">{t('send student')}</p>
                </button>
              </div>
            </div>

            {(currentICL || updateId) && (
              <ExamTasksForm
                exam={currentICL || updateIclInitValue}
                updateICL={!!updateId}
                currentICLId={currentICL?.id}
                tasks={tasks}
                setTasks={setTasks}
                workType={WorkType.exam}
              />
            )}
          </div>
        </Form>
      </FormikProvider>
      <SendToStudentsModal currentICL={currentICL || updateIclInitValue} isOpen={isOpen} onClose={close} />
    </LoadingError>
  );
}
